import { addProjectInstallation } from '@api/services/project-installation.service';
import { useMutation } from '@tanstack/react-query';
import moment from 'moment';
import * as Yup from 'yup';
import { ProjectInstallationStatusEnum } from '@api/enum';
import { IBusinessDevelopmentUser, IClientInstallation } from '@api/models';

// ✅ Explicit type
export type TProjectInstallationFormValues = {
    name: string;
    poc: {
        name: string;
        email: string;
        phoneNumber: number | string;
        designation: string;
    }[];
    location: {
        country: string;
        latitude: string;
        longitude: string;
        googleMapsLink: string;
        city: string;
    };
    contract: {
        startDate: string;
        endDate: string;
    };
    businessDevelopmentUser?: string;
    // projectMilestone: boolean;
    status: ProjectInstallationStatusEnum;
    files: File[];
};

const useProjectInstallationAddForm = (clientInstallationId: string, clientInstallation: IClientInstallation) => {
    const schema = Yup.object({
        name: Yup.string().required('Name is required'),
        poc: Yup.array().of(
            Yup.object({
                name: Yup.string().required('Name is required'),
                email: Yup.string().email('Invalid email').required('Email is required'),
                phoneNumber: Yup.number().nullable(),
                designation: Yup.string().nullable(),
            })
        ),
        location: Yup.object({
            country: Yup.string().required('Country is required'),
            city: Yup.string().required('City is required'),
            latitude: Yup.string().optional(),
            longitude: Yup.string().optional(),
            googleMapsLink: Yup.string().url('Enter a valid URL').optional(),
        }),
        contract: Yup.object({
            startDate: Yup.string()
                .nullable()
                .test('valid-startDate', 'Invalid start date', (value) => {
                    if (!value) return true; // allow empty string or null
                    return moment(value, 'YYYYMMDD', true).isValid();
                }),

            endDate: Yup.string()
                .nullable()
                .test('valid-endDate', 'Invalid end date', (value) => {
                    if (!value) return true; // allow empty string or null
                    return moment(value, 'YYYYMMDD', true).isValid();
                })
                .test('end-after-start', 'End date must be same or after start date', function (endDate) {
                    const { startDate } = this.parent;

                    if (!startDate || !endDate) return true; // Allow empty or null start or end date
                    const start = moment(startDate, 'YYYYMMDD', true);
                    const end = moment(endDate, 'YYYYMMDD', true);

                    // Ensuring the end date is same or after the start date
                    return end.isSameOrAfter(start, 'day');
                }),
        }),
        // projectMilestone: Yup.boolean().required(),
        status: Yup.mixed<ProjectInstallationStatusEnum>()
            .oneOf(Object.values(ProjectInstallationStatusEnum))
            .required('Status is required'),
        businessDevelopmentUser: Yup.string().required('This field is required'),

        files: Yup.array().of(Yup.mixed()),
    });

    const initialValues: TProjectInstallationFormValues = {
        name: '',
        poc:
            clientInstallation?.poc?.map((poc) => ({
                name: poc.name || '',
                email: poc.email || '',
                phoneNumber: poc.phoneNumber || '',
                designation: poc.designation || '',
            })) || [],
        location: {
            country: clientInstallation.location.country,
            city: '',
            latitude: '',
            longitude: '',
            googleMapsLink: '',
        },
        contract: {
            startDate: '',
            endDate: '',
        },
        businessDevelopmentUser: (clientInstallation.businessDevelopmentUser as IBusinessDevelopmentUser)._id,

        // projectMilestone: false,
        status: ProjectInstallationStatusEnum.PENDING,
        files: [],
    };

    const mutation = useMutation((payload: FormData) => addProjectInstallation(clientInstallationId, payload));

    const submit = async (values: TProjectInstallationFormValues) => {
        const formData = new FormData();

        formData.append('name', values.name);
        formData.append('status', values.status);
        // formData.append('projectMilestone', String(values.projectMilestone));
        formData.append('businessDevelopmentUser', values.businessDevelopmentUser || '');

        values.poc.forEach((poc, index) => {
            formData.append(`poc[${index}][name]`, poc.name);
            formData.append(`poc[${index}][email]`, poc.email);
            formData.append(`poc[${index}][phoneNumber]`, poc.phoneNumber.toString());
            if (poc.designation) {
                formData.append(`poc[${index}][designation]`, poc.designation);
            }
        });

        if (values.location.country) formData.append('location[country]', values.location.country);
        if (values.location.city) formData.append('location[city]', values.location.city);
        if (values.location.latitude) formData.append('location[latitude]', values.location.latitude);
        if (values.location.longitude) formData.append('location[longitude]', values.location.longitude);
        if (values.location.googleMapsLink) formData.append('location[googleMapsLink]', values.location.googleMapsLink);

        if (values.contract.startDate) {
            formData.append('contract[startDate]', values.contract.startDate);
        }

        if (values.contract.endDate) {
            formData.append('contract[endDate]', values.contract.endDate);
        }

        values.files?.forEach((file: File) => {
            formData.append('files', file);
        });

        const { data } = await mutation.mutateAsync(formData);
        return data;
    };

    return {
        initialValues,
        schema,
        isSubmitting: mutation.isLoading,
        submit,
    };
};

export default useProjectInstallationAddForm;
